// «Дашборд»: сколько всего в аккаунте, расход дневных лимитов ИИ и последние действия.
import { useEffect, useState } from "react";
import { boot } from "../boot";
import { Icon } from "../components/Icon";
import { loadMe, type Me, type Usage } from "../lib/account";
import { shortDate } from "../lib/text";
import { chatsStore, docName, foldersStore, savedStore, uploadedStore, useStore } from "../store";

// Полоса лимита: заливка и дорожка одного цвета; к концу лимита — предупреждение, на исходе — красная. Число — всегда подписью.
function Meter({ title, usage, hint }: { title: string; usage: Usage; hint: string }) {
  const share = usage.limit ? Math.min(1, usage.used / usage.limit) : 0;
  const level = share >= 1 ? "is-out" : share >= 0.8 ? "is-low" : "";
  return (
    <div className="dash-meter">
      <div className="dash-meter-head"><span>{title}</span><strong>{usage.used} из {usage.limit}</strong></div>
      <div className={`dash-meter-track ${level}`} role="meter" aria-label={title} aria-valuemin={0} aria-valuemax={usage.limit} aria-valuenow={usage.used}
        aria-valuetext={`${usage.used} из ${usage.limit}`}>
        <span style={{ width: `${share * 100}%` }} />
      </div>
      <p className="dash-meter-hint">{share >= 1 ? "Лимит на сегодня исчерпан — завтра он обновится." : hint}</p>
    </div>
  );
}

export function DashboardPage() {
  const saved = useStore(savedStore);
  const uploaded = useStore(uploadedStore);
  const chats = useStore(chatsStore);
  const folders = useStore(foldersStore);
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => { loadMe().then(setMe).catch(() => setMe(null)); }, []);

  const tiles = [
    { label: "Чаты", value: chats.length, icon: "chat", href: "/" },
    { label: "Собранные документы", value: saved.doc.length, icon: "doc", href: "/my" },
    { label: "Загруженные файлы", value: uploaded.length, icon: "upload", href: "/my" },
    { label: "Расчёты госпошлины", value: saved.calc.length, icon: "calc", href: "/my" },
    { label: "Папки", value: folders.length, icon: "folder", href: "/my" },
  ];
  const recent = [
    ...chats.map((chat) => ({ key: `c${chat.id}`, date: chat.updated_at, title: chat.title, kind: "Чат", icon: "chat", href: `/?chat=${chat.id}` })),
    ...saved.doc.map((item) => ({ key: `d${item.id}`, date: item.created_at, title: item.title, kind: "Документ", icon: "doc", href: "/my" })),
    ...saved.calc.map((item) => ({ key: `k${item.id}`, date: item.created_at, title: item.title, kind: "Расчёт", icon: "calc", href: `/?start=calc:${item.id}` })),
    ...uploaded.map((doc) => ({ key: `u${doc.id}`, date: doc.created_at, title: docName(doc), kind: "Загрузка", icon: "upload", href: `/?start=review:${doc.id}` })),
  ].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6);
  const guest = boot.isGuest;

  return (
    <div className="dash-page">
      <h1>Дашборд</h1>
      <p className="lead">Всё, что есть в вашем аккаунте, и сколько осталось на сегодня.</p>

      <ul className="dash-tiles">
        {tiles.map((tile) => (
          <li key={tile.label}>
            <a className="dash-tile" href={tile.href}>
              <span className="dash-tile-icon"><Icon name={tile.icon} /></span>
              <span className="dash-tile-value">{tile.value}</span>
              <span className="dash-tile-label">{tile.label}</span>
            </a>
          </li>
        ))}
      </ul>

      <section className="dash-card" aria-labelledby="dash-limits">
        <h2 id="dash-limits">Сегодня</h2>
        {me ? (
          <div className="dash-meters">
            <Meter title="Сообщения помощнику" usage={me.usage.assistant}
              hint={guest ? "После входа лимит — 100 сообщений в день." : "Лимит обновляется каждый день."} />
            <Meter title="Вопросы мастера документов" usage={me.usage.documents}
              hint={guest ? "После входа лимит — 300 вопросов в день." : "Лимит обновляется каждый день."} />
          </div>
        ) : <p className="dash-empty">Загружаю…</p>}
        {!boot.aiEnabled && <p className="dash-meter-hint">ИИ сейчас не подключён: чат работает по ключевым словам, лимиты не расходуются.</p>}
      </section>

      <section className="dash-card" aria-labelledby="dash-recent">
        <h2 id="dash-recent">Последние действия</h2>
        {recent.length ? (
          <ul className="dash-recent">
            {recent.map((item) => (
              <li key={item.key}>
                <a href={item.href}>
                  <span className="dash-recent-icon"><Icon name={item.icon} /></span>
                  <span className="dash-recent-title">{item.title}</span>
                  <span className="dash-recent-meta">{item.kind} · {shortDate(item.date)}</span>
                </a>
              </li>
            ))}
          </ul>
        ) : <p className="dash-empty">Пока пусто. Начните чат, соберите документ или загрузите файл.</p>}
      </section>
    </div>
  );
}
